import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  useAddNetworkAdminMutation,
  useRemoveNetworkAdminMutation,
  useResendNetworkAdminInviteMutation,
  useUpdateLinkedinSyncMutation,
} from 'services/networkAdmins';
import { useGetNetworkAdminsQuery } from 'services/networkAdminV2';
import { useGetUserQuery } from 'services/user';
import { trackEvent } from 'helpers/analytics';
import { useResendAllyInviteMutation } from '../../services/allies';
import { activeNetworkSelector } from '../../redux/selectors';
import { useSignOut } from '../useSignOut';

export const useNetworkAdmins = () => {
  const { signOut } = useSignOut();
  const network = useSelector(activeNetworkSelector);
  const {
    data: admins = [],
    isLoading: isLoadingNetworkAdmins,
    isError: hasLoadAdminsListError,
  } = useGetNetworkAdminsQuery({ networkId: network.id, includeAllies: true });
  const [resend, { isLoading: isLoadingResendInvite, isError: hasResendInviteError }] =
    useResendNetworkAdminInviteMutation();
  const [resendAllyInvite, { isLoading: isLoadingResendAllyInvite, isError: hasResendAllyInviteError }] =
    useResendAllyInviteMutation();
  const [remove, { isLoading: isLoadingRemoveAdmin, isError: hasRemoveAdminError }] = useRemoveNetworkAdminMutation();
  const { data } = useGetUserQuery();
  const [add, { error: hasAddAdminError, isLoading: isLoadingAddAdmin }] = useAddNetworkAdminMutation();
  const [updateLinkedinSync, { error: hasUpdateLinkedinSyncError, isLoading: isLoadingLinkedinSync }] =
    useUpdateLinkedinSyncMutation();

  const resendInvite = useCallback(
    async (user) => {
      const method = user.role === 'ally' ? resendAllyInvite : resend;
      const { error, data: responseData } = await method({ networkId: network.id, adminId: user.id });
      if (error) return { error };

      trackEvent('team_members:resend_invite', {
        collectionId: network.id,
        isNetworkAdmin: !!data.managingCollections.length,
        isOrganizationAdmin: !!data.managingOrganizations.length,
        resendUserRole: user.role === 'ally' ? 'ally' : 'network_admin',
      });

      return responseData;
    },
    [resendAllyInvite, resend, network, data],
  );

  const removeAdmin = useCallback(
    async (admin) => {
      const { error, data: responseData } = await remove({ adminId: admin.id, role: admin.role });
      if (error) return { error };

      trackEvent('team_members:remove', {
        collectionId: network.id,
        isNetworkAdmin: !!data.managingCollections.length,
        isOrganizationAdmin: !!data.managingOrganizations.length,
        removedUserRole: admin.role === 'ally' ? 'ally' : 'network_admin',
      });

      if (responseData.id === admin.id) {
        signOut();
      }

      return responseData;
    },
    [data, network, remove, signOut],
  );

  const addAdmin = useCallback(
    async (admin) => {
      const { error, data: responseData } = await add({ admin, networkId: network.id });
      if (error) return { error };

      trackEvent('team_members:invite_member', {
        collectionId: network.id,
        isNetworkAdmin: !!data.managingCollections.length,
        isOrganizationAdmin: !!data.managingOrganizations.length,
        invitedUserRole: admin.role === 'ally' ? 'ally' : 'network_admin',
      });

      return { data: responseData };
    },
    [network, data, add],
  );

  const updateLinkedinSyncState = useCallback(
    async (admin, state) => {
      const { error, data: responseData } = await updateLinkedinSync({ adminId: admin.id, state });
      if (error) return { error };

      return responseData;
    },
    [updateLinkedinSync],
  );

  return {
    hasLoadAdminsListError,
    hasResendInviteError: hasResendInviteError || hasResendAllyInviteError,
    hasRemoveAdminError,
    hasAddAdminError,
    hasUpdateLinkedinSyncError,
    admins,
    isLoadingNetworkAdmins,
    isLoadingResendInvite: isLoadingResendInvite || isLoadingResendAllyInvite,
    isLoadingRemoveAdmin,
    isLoadingAddAdmin,
    isLoadingLinkedinSync,
    resendInvite,
    removeAdmin,
    addAdmin,
    updateLinkedinSync: updateLinkedinSyncState,
  };
};
